/**
 * The contents page.
 *
 * Everything this kind is: how it is made, how it is read back, what it looks
 * like on the page, and how it is edited. Nothing outside this folder needs to
 * know the kind exists.
 */

import { isJsonObject, readNumber, readString } from '@/domain/json'
import type { JsonValue } from '@/domain/json'
import { type ContentsSection } from '@/domain/section'
import ContentsBlock from '@/sections/contents/ContentsBlock.vue'
import ContentsInspector from '@/sections/contents/ContentsInspector.vue'
import type { SectionDefinition } from '@/sections/types'

function readDepth(stored: JsonValue): 1 | 2 | 3 {
  const depth = readNumber(stored, 'depth', 2)
  return depth === 1 || depth === 3 ? depth : 2
}

/** The text style of each of the first three levels, by id; empty where there is none. */
function readLevelStyles(stored: JsonValue): readonly [string, string, string] {
  const source = isJsonObject(stored) ? stored['levelStyleIds'] : undefined
  const at = (index: number): string => {
    const value = Array.isArray(source) ? source[index] : undefined
    return typeof value === 'string' ? value : ''
  }
  return [at(0), at(1), at(2)]
}

export const contentsSection: SectionDefinition<'contents', ContentsSection> = {
  kind: 'contents',
  band: 'structure',

  icon: ['M3 4.5h6', 'M12 4.5h1', 'M3 8h7', 'M12.5 8h.5', 'M3 11.5h5', 'M12 11.5h1'],

  create: (id, title) => ({
    id,
    kind: 'contents',
    title,
    depth: 2,
    titleStyleId: '',
    subtitle: '',
    pageNumbers: true,
    showDetail: true,
    levelStyleIds: ['', '', ''],
  }),

  read: ({ id, title, stored }) => ({
    id,
    kind: 'contents',
    title,
    depth: readDepth(stored),
    titleStyleId: readString(stored, 'titleStyleId'),
    subtitle: readString(stored, 'subtitle'),
    // On unless somebody turned them off: a contents page written before these
    // settings existed reads as it always did, and gains page numbers.
    pageNumbers: isJsonObject(stored) && stored['pageNumbers'] === false ? false : true,
    showDetail: isJsonObject(stored) && stored['showDetail'] === false ? false : true,
    levelStyleIds: readLevelStyles(stored),
  }),

  block: ContentsBlock,
  inspector: ContentsInspector,
}
